import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Search } from "lucide-react";
import { toast } from "sonner";
import { Input } from "../../components/ui/input";
import { PageHeader } from "../../components/admin/PageHeader";
import { DataTable, Pagination } from "../../components/admin/DataTable";
import { ConfirmDialog } from "../../components/admin/ConfirmDialog";
import { useAuth } from "../../context/AuthContext";
import { canDo, staffDirectory, fmtDateTime } from "../../lib/adminApi";
import { supabase } from "../../lib/supabase";

const PAGE_SIZE = 20;
const ACTIONS = ["No action needed", "Warned the member", "Profile edited", "Profile put on hold", "Profile blocked", "Share revoked", "Other"];

export default function ReportsFlags() {
  const auth = useAuth();
  const [status, setStatus] = useState("open");
  const [q, setQ] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [asc, setAsc] = useState(false);
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ rows: [], count: 0 });
  const [staff, setStaff] = useState({});
  const [resolving, setResolving] = useState(null);
  const [actionTaken, setActionTaken] = useState(ACTIONS[0]);

  const load = useCallback(async () => {
    let query = supabase.from("reports").select("*, profiles!inner(id, full_name, registration_no)", { count: "exact" });
    if (status) query = query.eq("status", status);
    if (from) query = query.gte("created_at", `${from}T00:00:00+05:30`);
    if (to) query = query.lte("created_at", `${to}T23:59:59+05:30`);
    const term = q.replace(/[,()*%\\:"]/g, " ").trim();
    if (term) query = query.or(`full_name.ilike.*${term}*,registration_no.ilike.*${term}*`, { referencedTable: "profiles" });
    const start = (page - 1) * PAGE_SIZE;
    const { data: rows, error, count } = await query.order("created_at", { ascending: asc }).range(start, start + PAGE_SIZE - 1);
    if (error) toast.error(error.message); else setData({ rows, count: count || 0 });
  }, [status, q, from, to, asc, page]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);
  useEffect(() => { staffDirectory().then(setStaff); }, []);

  const startResolve = (r) => {
    setActionTaken(ACTIONS[0]);
    setResolving({
      title: "Resolve report", description: `Report on ${r.profiles.full_name}: "${r.reason}"`, reasonOptional: true, confirmLabel: "Mark resolved",
      run: async (notes) => {
        const { error } = await supabase.from("reports").update({ status: "resolved", action_taken: document.getElementById("resolve-action").value, resolution_notes: notes || null, resolved_by: auth.user.id, resolved_at: new Date().toISOString() }).eq("id", r.id);
        if (error) toast.error(error.message); else { toast.success("Report resolved"); load(); }
      },
    });
  };

  const columns = [
    { key: "profile", label: "Profile", render: (r) => <Link to={`/admin/profiles/${r.profiles.id}`} className="font-semibold text-[#7A0016] hover:underline">{r.profiles.full_name} · {r.profiles.registration_no}</Link> },
    { key: "reason", label: "Reason", render: (r) => <span className="block max-w-xs truncate" title={r.reason}>{r.reason}</span> },
    { key: "by", label: "Reported by", render: (r) => staff[r.reported_by] ? `Staff (${staff[r.reported_by]})` : "Client" },
    { key: "created_at", label: "Reported", sortable: true, render: (r) => fmtDateTime(r.created_at) },
    { key: "status", label: "Status", render: (r) => <span data-testid={`report-status-${r.id}`} className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ${r.status === "open" ? "bg-red-50 text-red-800" : "bg-emerald-50 text-emerald-800"}`}>{r.status}</span> },
    { key: "resolution", label: "Resolution", render: (r) => (r.status === "resolved" ? <span className="block max-w-xs truncate text-xs" title={r.resolution_notes || ""}>{r.action_taken}{r.resolution_notes ? ` — ${r.resolution_notes}` : ""} ({staff[r.resolved_by] || "staff"}, {fmtDateTime(r.resolved_at)})</span> : "-") },
  ];
  return (
    <div data-testid="reports-flags-page">
      <PageHeader eyebrow="Moderation" title="Reports & Flags" />
      <div className="mb-4 flex flex-wrap items-end gap-3">
        <select value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }} data-testid="reports-status-filter" className="rmm-select rmm-input h-10 w-36">
          <option value="open">Open</option><option value="resolved">Resolved</option><option value="">All</option>
        </select>
        <div className="relative min-w-[200px] flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#8C6A6C]" />
          <Input value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} placeholder="Search profile name or registration no." data-testid="reports-search-input" className="rmm-input h-10 bg-white pl-9" />
        </div>
        <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="reports-from" className="rmm-input h-10 w-40 bg-white" />
        <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} data-testid="reports-to" className="rmm-input h-10 w-40 bg-white" />
      </div>
      <DataTable columns={columns} rows={data.rows} sort={{ key: "created_at", dir: asc ? "asc" : "desc" }} onSort={() => setAsc((a) => !a)}
        rowActions={(r) => (r.status === "open" && canDo(auth, "can_edit") ? <button type="button" onClick={() => startResolve(r)} data-testid={`resolve-${r.id}`} className="text-xs font-semibold text-[#7A0016] hover:underline">Resolve</button> : null)} />
      <Pagination page={page} pageSize={PAGE_SIZE} count={data.count} onPage={setPage} />
      <ConfirmDialog action={resolving} onClose={() => setResolving(null)} extra={
        <label className="block text-sm font-medium text-[#2C2424]">Action taken
          <select id="resolve-action" value={actionTaken} onChange={(e) => setActionTaken(e.target.value)} data-testid="resolve-action-select" className="rmm-select rmm-input mt-1 h-10 w-full">
            {ACTIONS.map((a) => <option key={a}>{a}</option>)}
          </select>
        </label>
      } />
    </div>
  );
}
